import { calculateTip, getTipAmount, useCheckoutPlan } from '@shamaazi/mytennights'
import { type FC, useEffect, useState } from 'react'
import editBlack from '~/assets/icons/edit-secondary-5.svg'
import editGray from '~/assets/icons/edit-gray.svg'
import { displayTidyWithoutCurrency, GetCurrencyByString, getCurrencySymbol, Tip } from '@lib/services'
import CurrencyInput from 'react-currency-input-field'
import { MytennightsTestId } from '@lib/testing'

export const TipSelection: FC = () => {
  const tipPercentages: Partial<Record<Tip, number>> = {
    [Tip.FivePercent]: 5,
    [Tip.TenPercent]: 10,
    [Tip.TwentyPercent]: 20,
    [Tip.Other]: 0 // Leave empty for "Other" button with pencil icon
  }
  const { plan, setPlan } = useCheckoutPlan()
  const [customTipError, setCustomTipError] = useState('')

  // adjust the tipValue whenever the donationTotal / tipPercent changes
  useEffect(() => {
    if (plan.paymentDetails.tipPercent === Tip.Other) {
      return
    }
    const tipValue = calculateTip(plan.causes, tipPercentages[plan.paymentDetails.tipPercent] ?? 0)
    setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, tipValue } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.causes, plan.paymentDetails.tipPercent])

  const handleTipChange = (percent: Tip): void => {
    setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, tipPercent: percent, tipValue: getTipAmount(plan.causes, percent, 0) } })
  }

  const handleSelectManualTip = (): void => {
    setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, tipPercent: Tip.Other, tipValue: 0 } })
  }

  const handleCustomTipChange = (value: string): void => {
    const customTipAmount = Number(value)

    if (customTipAmount < 0) {
      setCustomTipError('Sorry, the minimum tip is 0.00')
      return
    }
    if (customTipAmount >= 999999) {
      setCustomTipError('Sorry, we don\'t accept tips this large!')
      return
    }
    setCustomTipError('')
    setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, tipPercent: Tip.Other, tipValue: customTipAmount * 100 } })
  }

  const renderSliderButton = (key: string, isSelected: boolean, onClick: () => void): JSX.Element => (
    <div
      key={key}
      className={`cursor-pointer rounded-full flex items-center justify-center transition
        ${isSelected ? 'w-6 h-6 bg-yellow-300 rounded-full ring-4 ring-mtd-primary filter drop-shadow-md' : 'w-5 h-5 bg-mtd-primary-1 border-2 border-mtd-beige-2 drop-shadow-none'}`}
      onClick={onClick}
    />
  )

  return <div>
    {/* Tip option labels */}
    <div className='px-2'>
    <div className="flex justify-between items-center w-full font-semibold text-lg">
      {[Tip.FivePercent, Tip.TenPercent, Tip.TwentyPercent].map((percent) => (
        <span key={percent}
          data-test-id={`${MytennightsTestId.tipSelection}${percent}`}
          className={`text-mtd-body-small cursor-pointer font-normal mx-1
                ${plan.paymentDetails.tipPercent === percent ? ' text-mtd-secondary-5' : ' text-mtd-gray-6'}`}
          onClick={() => handleTipChange(percent)}
        ><span className='font-bold'>{tipPercentages[percent]}</span>%</span>
      ))}
      <span onClick={handleSelectManualTip} data-test-id={`${MytennightsTestId.tipSelection}${Tip.Other}`}>
        <img src={plan.paymentDetails.tipPercent === Tip.Other ? editBlack : editGray} alt="Edit" width={18} height={18} />
      </span>
    </div>

    {/* Slider bar */}
    <div className="relative w-full max-w-md h-8 px-6 ">
      <div className="absolute top-1/2 left-0 right-0 transform -translate-y-1/2 h-6 bg-mtd-beige-2 border-2 border-mtd-beige-1 rounded-full flex items-center justify-between">
        {/* Dot Buttons */}
        {[Tip.FivePercent, Tip.TenPercent, Tip.TwentyPercent].map((percent) => {
          return renderSliderButton(percent, plan.paymentDetails.tipPercent === percent, () => handleTipChange(percent))
        })}
        {renderSliderButton(Tip.Other, plan.paymentDetails.tipPercent === Tip.Other, handleSelectManualTip)}
      </div>
    </div>
    </div>

    {plan.paymentDetails.tipPercent === Tip.Other &&
      <div className="relative mt-4">
        <span className="absolute font-bold transform -translate-y-1/2 pointer-events-none left-3 top-2/4 whitespace-nowrap text-fg-gray-850">
          {getCurrencySymbol(plan.charity.currency)}
        </span>
        <CurrencyInput
          data-test-id={MytennightsTestId.customTipInput}
          id="custom tip amount"
          className={`w-full text-left rounded-lg border border-mtd-gray-5 bg-white px-4 py-3 pl-11 font-bold text-mtd-secondary-5`}
          placeholder={'0.00'}
          step={0.01}
          value={plan.paymentDetails.tipValue / 100}
          onValueChange={(value) => handleCustomTipChange(value ?? '')}
          intlConfig={{ locale: 'en-UK' }}
        />
      </div>
    }

    {/* Display tip absolute amount */}
    {plan.paymentDetails.tipPercent !== Tip.Other &&
      <div className='mt-4 w-full text-center font-medium text-mtd-secondary-4'>
        <span>{getCurrencySymbol(GetCurrencyByString(plan.charity.currency))} </span>
        <span>{displayTidyWithoutCurrency(plan.paymentDetails.tipValue)}</span>
      </div>}

    {/* Error message */}
    {customTipError && <p className="text-mtn-red-100 text-xs font-normal mt-2">{customTipError}</p>}
  </div>
}
